import { ChevronRightIcon, MinusIcon, PlusIcon } from 'lucide-react'
import { useRef, useState } from 'react'

import { IconButton, Popover } from 'slab'

interface DependentCounterProps {
  count: number
  onIncrement: () => void
  onDecrement: () => void
  popoverContent?: React.ReactNode
  disabled?: boolean
}

export function DependentCounter ({
  count,
  onIncrement,
  onDecrement,
  popoverContent,
  disabled = false,
}: DependentCounterProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const ignoreNextCloseRef = useRef(false)

  const handleOpenChange = (open: boolean) => {
    // If we're trying to close and we should ignore it (because +/- was clicked), skip
    if (!open && ignoreNextCloseRef.current) {
      ignoreNextCloseRef.current = false
      return
    }
    setIsPopoverOpen(open)
  }

  return (
    <div className='flex items-center gap-2'>
      <div
        className={`
          flex items-center h-8 rounded-lg border border-gray-300
          ${count > 0 ? 'bg-gray-200' : 'bg-white'}
        `}
        style={{
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        }}
      >
        <button
          type='button'
          onPointerDown={() => {
            ignoreNextCloseRef.current = true
          }}
          onClick={(e) => {
            e.stopPropagation()
            onDecrement()
            setTimeout(() => {
              ignoreNextCloseRef.current = false
            }, 100)
          }}
          disabled={disabled || count === 0}
          className={`
            flex items-center justify-center w-8 h-full px-2
            border-r border-gray-300 rounded-l-lg
            bg-gradient-to-b from-gray-100 to-white
            hover:from-gray-200 hover:to-gray-100
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          `}
          aria-label='Remove dependent'
        >
          <MinusIcon size={12} className='text-gray-600' />
        </button>

        <div className='flex items-center justify-center w-8 h-full bg-white'>
          <span className='body-sm text-gray-800 text-center'>
            {count}
          </span>
        </div>

        <button
          type='button'
          onPointerDown={() => {
            ignoreNextCloseRef.current = true
          }}
          onClick={(e) => {
            e.stopPropagation()
            onIncrement()
            setTimeout(() => {
              ignoreNextCloseRef.current = false
            }, 100)
          }}
          disabled={disabled}
          className={`
            flex items-center justify-center w-8 h-full px-2
            border-l border-gray-300 rounded-r-lg
            bg-gradient-to-b from-gray-100 to-white
            hover:from-gray-200 hover:to-gray-100
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          `}
          aria-label='Add dependent'
        >
          <PlusIcon size={12} className='text-gray-600' />
        </button>
      </div>

      {count > 0 && popoverContent && (
        <Popover
          side='right'
          align='start'
          open={isPopoverOpen}
          onOpenChange={handleOpenChange}
          trigger={
            <IconButton
              icon={ChevronRightIcon}
              label='Edit dependent birthdays'
              disabled={disabled}
              size='small'
            />
          }
        >
          {popoverContent}
        </Popover>
      )}
    </div>
  )
}
